import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pressable, ScrollView, Text, View } from "react-native";
import tw from "twrnc";

import IconButton from "@/components/settings/IconButton";
import { colors, radius, text } from "@/components/settings/theme";
import { t } from "@/localization";
import { useWhatsNewActions } from "./actions";
import { useWhatsNewSession } from "./store";
import WhatsNewList from "./WhatsNewList";

// The What's new dropdown under the dashboard header button (web, wide
// layouts; phones open the What's new screen instead). Portalled to <body>
// like the help tooltips, so the header never clips it. Closes on a click
// outside, Escape or a resize.

const WIDTH = 400;
const GAP = 8;

const Panel = ({ onClose, pos }) => {
  const wn = useWhatsNewSession("header");
  const actions = useWhatsNewActions("header", onClose);
  const panelRef = useRef(null);

  useEffect(() => {
    panelRef.current?.focus?.();
  }, []);

  return (
    <View
      ref={panelRef}
      role="dialog"
      aria-label={t("What's new")}
      tabIndex={-1}
      style={{
        position: "fixed",
        zIndex: 100000,
        top: pos.top,
        right: pos.right,
        width: pos.width,
        maxHeight: pos.maxHeight,
        backgroundColor: colors.white,
        borderRadius: radius.card,
        borderWidth: 1,
        borderColor: colors.divider,
        boxShadow: "0 12px 32px rgba(17,24,39,0.18)",
        overflow: "hidden",
        outlineStyle: "none",
      }}
    >
      <View
        style={[
          tw`flex-row items-center`,
          {
            paddingLeft: 16,
            paddingRight: 8,
            paddingVertical: 10,
            borderBottomWidth: 1,
            borderBottomColor: colors.divider,
          },
        ]}
      >
        <View style={tw`flex-1`}>
          <Text accessibilityRole="header" style={text.sectionTitle}>
            {t("What's new")}
          </Text>
        </View>
        <IconButton
          icon="close"
          label={t("Close What's new")}
          onPress={onClose}
        />
      </View>
      <ScrollView style={{ flexGrow: 0 }}>
        <WhatsNewList
          entries={wn.entries}
          fresh={wn.fresh}
          compact
          gutter={16}
          actions={actions}
        />
      </ScrollView>
    </View>
  );
};

const WhatsNewMenu = ({ anchorRef, open, onClose }) => {
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const node = anchorRef.current;
    if (!node?.getBoundingClientRect) return;
    const r = node.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(WIDTH, vw - 16);
    const right = Math.max(8, Math.min(vw - r.right - 4, vw - width - 8));
    const top = r.bottom + GAP;
    setPos({ top, right, width, maxHeight: Math.max(240, vh - top - 16) });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    // Capture phase, so Escape closes only the dropdown and doesn't reach
    // the app's own Escape = Back handler (AppRouting).
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onClose);
    };
  }, [open, place, onClose]);

  if (!open || !pos || typeof document === "undefined") return null;

  return createPortal(
    <>
      <Pressable
        accessibilityLabel={t("Close What's new")}
        onPress={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          cursor: "default",
        }}
      />
      <Panel onClose={onClose} pos={pos} />
    </>,
    document.body
  );
};

export default WhatsNewMenu;
